import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Math as MathFormula } from "@/components/Math";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";
import { 
  FileText, Download, Save, Copy, Check, Plus, Trash2, 
  BookOpen, FileCode, ClipboardList, FileSpreadsheet,
  Brain, Sparkles, ArrowRight, Lock, Printer,
  ZoomIn, ZoomOut, Maximize2, Minimize2, X,
  Wand2, Loader2, AlertCircle, ChevronDown, Expand, Minimize, ListPlus
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Document templates
const templates = {
  blank: {
    name: "Blank Document",
    icon: FileText,
    title: "",
    sections: [{ title: "", content: "" }]
  },
  lessonPlan: {
    name: "Lesson Plan",
    icon: BookOpen,
    title: "Lesson Plan: [Topic]",
    sections: [
      { title: "Learning Objectives", content: "By the end of this lesson, students will be able to:\n\\begin{itemize}\n  \\item Objective 1\n  \\item Objective 2\n  \\item Objective 3\n\\end{itemize}" },
      { title: "Materials Needed", content: "\\begin{itemize}\n  \\item Material 1\n  \\item Material 2\n\\end{itemize}" },
      { title: "Introduction (10 min)", content: "Hook/attention getter:\n\nPrior knowledge activation:" },
      { title: "Direct Instruction (15 min)", content: "Key concepts to cover:\n\nExample problem:\n$$E = mc^2$$" },
      { title: "Guided Practice (15 min)", content: "Practice problems:\n\\begin{enumerate}\n  \\item Problem 1\n  \\item Problem 2\n\\end{enumerate}" },
      { title: "Independent Practice (10 min)", content: "Assignment/homework:" },
      { title: "Assessment", content: "Exit ticket questions:" }
    ]
  },
  assessment: {
    name: "Assessment",
    icon: ClipboardList,
    title: "Assessment: [Topic]",
    sections: [
      { title: "Instructions", content: "Read each question carefully. Show all work for full credit." },
      { title: "Multiple Choice", content: "\\textbf{1.} What is the formula for kinetic energy?\n\n\\begin{enumerate}[(a)]\n  \\item $E = mc^2$\n  \\item $KE = \\frac{1}{2}mv^2$\n  \\item $PE = mgh$\n  \\item $F = ma$\n\\end{enumerate}" },
      { title: "Short Answer", content: "\\textbf{2.} Explain the relationship between force and acceleration.\n\n\\vspace{3cm}" },
      { title: "Problem Solving", content: "\\textbf{3.} A ball is thrown upward with initial velocity 20 m/s. Calculate:\n\n(a) Maximum height reached\n\n(b) Time to reach maximum height\n\n\\textbf{Solution:}\n\n$$v^2 = v_0^2 - 2gh$$" },
      { title: "Answer Key", content: "1. (b)\n\n2. Newton's second law states $F = ma$...\n\n3. (a) $h = \\frac{v_0^2}{2g} = \\frac{400}{19.6} = 20.4$ m" }
    ]
  },
  report: {
    name: "Technical Report",
    icon: FileCode,
    title: "Technical Report: [Title]",
    sections: [
      { title: "Abstract", content: "Brief summary of the report's purpose, methods, and conclusions." },
      { title: "Introduction", content: "Background information and objectives of this report." },
      { title: "Theory", content: "The fundamental equation governing this phenomenon is:\n$$F = ma$$\n\nwhere $F$ is force, $m$ is mass, and $a$ is acceleration." },
      { title: "Methods", content: "Experimental procedure and data collection methods." },
      { title: "Results", content: "Data and observations:\n\n\\begin{tabular}{|c|c|c|}\n\\hline\nTrial & Time (s) & Distance (m) \\\\\n\\hline\n1 & 2.5 & 10.2 \\\\\n2 & 2.7 & 11.1 \\\\\n\\hline\n\\end{tabular}" },
      { title: "Discussion", content: "Analysis of results and comparison with theoretical predictions." },
      { title: "Conclusion", content: "Summary of findings and their implications." },
      { title: "References", content: "\\begin{enumerate}\n  \\item Author, A. (Year). Title. Journal.\n\\end{enumerate}" }
    ]
  },
  quickNotes: {
    name: "Quick Notes",
    icon: FileSpreadsheet,
    title: "Notes: [Topic]",
    sections: [
      { title: "Key Concepts", content: "\\textbf{Concept 1:}\n\n\\textbf{Concept 2:}" },
      { title: "Formulas", content: "$$\\text{Formula 1}$$\n$$\\text{Formula 2}$$" },
      { title: "Examples", content: "\\textbf{Example 1:}\n\n\\textbf{Example 2:}" },
      { title: "Practice Problems", content: "1.\n\n2.\n\n3." }
    ]
  }
};

interface Section {
  title: string;
  content: string;
}

export default function LaTeXDocumentEditor() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Tools", url: "https://calclab.app/tools" },
      { name: "LaTeX Document Editor", url: "https://calclab.app/tools/latex-editor" }
    ], lang),
    (lang) => calculatorToolSchema(
      "LaTeX Document Editor",
      "Create professional LaTeX documents with templates for lesson plans, assessments, and technical reports. Free online editor with live preview.",
      "https://calclab.app/tools/latex-editor",
      lang
    )
  ]);

  const { user } = useAuth();
  const { role } = useUserRole();
  const hasAccess = role === 'student' || role === 'paid' || role === 'admin';

  const [documentTitle, setDocumentTitle] = useState("");
  const [sections, setSections] = useState<Section[]>([{ title: "", content: "" }]);
  const [activeSection, setActiveSection] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // AI generation state
  const [aiContext, setAiContext] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<keyof typeof templates | null>(null);

  const zoomLevels = [75, 100, 125, 150];

  const canUseAI = user && hasAccess;

  useEffect(() => {
    if (user && hasAccess) {
      fetchGroups();
    }
  }, [user, hasAccess]);

  const fetchGroups = async () => {
    const { data } = await supabase
      .from('library_groups')
      .select('id, name')
      .eq('archived', false)
      .order('name');
    if (data) setGroups(data);
  };

  const applyTemplate = (templateKey: keyof typeof templates) => {
    const template = templates[templateKey];
    setDocumentTitle(template.title);
    setSections(template.sections.map(s => ({ ...s })));
    setActiveSection(0);
    setActiveTemplate(templateKey);
    toast.success(`Applied "${template.name}" template`);
  };

  // AI Generation Functions
  const generateWithAI = async (
    generationType: 'section' | 'expand' | 'simplify' | 'examples' | 'full_document',
    sectionIndex?: number
  ) => {
    if (!canUseAI) {
      toast.error("Please sign in with a membership to use AI features");
      return;
    }

    // Validate context is provided
    if (!aiContext.trim()) {
      toast.error("Please enter a description or prompt in the AI Context field first");
      return;
    }

    // For section generation, require section title or document title
    if (generationType === 'section' && !documentTitle && !sections[sectionIndex || activeSection].title) {
      toast.error("Please enter a document title or section title before generating content");
      return;
    }

    setIsGenerating(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-document-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session?.access_token}`,
        },
        body: JSON.stringify({
          documentTitle,
          sectionTitle: sections[sectionIndex ?? activeSection]?.title || '',
          context: aiContext,
          generationType,
          templateType: activeTemplate || 'lessonPlan',
          existingContent: sections[sectionIndex ?? activeSection]?.content || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate content');
      }

      const data = await response.json();
      
      if (generationType === 'full_document') {
        // Parse the full document and distribute to sections
        const content = data.content;
        // For now, put all content in the first section
        const newSections = [...sections];
        newSections[0] = { ...newSections[0], content };
        setSections(newSections);
        toast.success("Document generated! Review and edit as needed.");
      } else {
        // Update the specific section
        const idx = sectionIndex ?? activeSection;
        const newSections = [...sections];
        if (generationType === 'expand' || generationType === 'examples') {
          // Append to existing content
          newSections[idx] = { 
            ...newSections[idx], 
            content: newSections[idx].content + '\n\n' + data.content 
          };
        } else {
          // Replace content
          newSections[idx] = { ...newSections[idx], content: data.content };
        }
        setSections(newSections);
        toast.success("Content generated!");
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  const addSection = () => {
    setSections([...sections, { title: "", content: "" }]);
    setActiveSection(sections.length);
  };

  const removeSection = (index: number) => {
    if (sections.length === 1) {
      toast.error("Cannot remove the last section");
      return;
    }
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
    if (activeSection >= newSections.length) {
      setActiveSection(newSections.length - 1);
    }
  };

  const updateSection = (index: number, field: 'title' | 'content', value: string) => {
    const newSections = [...sections];
    newSections[index][field] = value;
    setSections(newSections);
  };

  const generateFullDocument = () => {
    let doc = "";
    if (documentTitle) {
      doc += `\\title{${documentTitle}}\n\\maketitle\n\n`;
    }
    sections.forEach(section => {
      if (section.title) {
        doc += `\\section{${section.title}}\n`;
      }
      doc += section.content + "\n\n";
    });
    return doc.trim();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generateFullDocument());
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const exportAsLatex = () => {
    const content = generateFullDocument();
    const fullDoc = `\\documentclass{article}
\\usepackage{amsmath}
\\usepackage{amssymb}
\\usepackage{graphicx}

\\begin{document}

${content}

\\end{document}`;
    
    const blob = new Blob([fullDoc], { type: 'text/x-latex' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentTitle || 'document'}.tex`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded as LaTeX (.tex)");
  };

  const exportAsHtml = () => {
    // Simple HTML export for Google Docs compatibility
    let html = `<!DOCTYPE html>
<html>
<head>
<title>${documentTitle || 'Document'}</title>
<script src="https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/katex.min.js"></script>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/katex.min.css">
</head>
<body style="font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 20px;">
`;
    if (documentTitle) {
      html += `<h1 style="text-align: center;">${documentTitle}</h1>\n`;
    }
    sections.forEach(section => {
      if (section.title) {
        html += `<h2>${section.title}</h2>\n`;
      }
      html += `<div>${section.content.replace(/\n/g, '<br>')}</div>\n`;
    });
    html += `</body></html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentTitle || 'document'}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded as HTML (for Google Docs)");
  };

  const printAsPdf = () => {
    // Create a print-friendly version in a new window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }

    const printContent = `<!DOCTYPE html>
<html>
<head>
  <title>${documentTitle || 'Document'} - CalcLab</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/katex.min.css">
  <script src="https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/katex.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/contrib/auto-render.min.js"></script>
  <style>
    @page {
      size: letter;
      margin: 1in;
    }
    body {
      font-family: 'Times New Roman', Georgia, serif;
      font-size: 12pt;
      line-height: 1.6;
      color: #000;
      max-width: 100%;
      padding: 0;
      margin: 0;
    }
    h1 {
      text-align: center;
      font-size: 18pt;
      margin-bottom: 24pt;
      border-bottom: 1px solid #000;
      padding-bottom: 12pt;
    }
    h2 {
      font-size: 14pt;
      margin-top: 18pt;
      margin-bottom: 12pt;
      color: #000;
    }
    .section {
      margin-bottom: 18pt;
    }
    .content {
      white-space: pre-wrap;
      margin-bottom: 12pt;
    }
    .katex { font-size: 1em; }
    .katex-display { margin: 1em 0; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  ${documentTitle ? `<h1>${documentTitle}</h1>` : ''}
  ${sections.map(section => `
    <div class="section">
      ${section.title ? `<h2>${section.title}</h2>` : ''}
      <div class="content">${section.content}</div>
    </div>
  `).join('')}
  <script>
    document.addEventListener('DOMContentLoaded', function() {
      renderMathInElement(document.body, {
        delimiters: [
          {left: '$$', right: '$$', display: true},
          {left: '$', right: '$', display: false},
          {left: '\\\\[', right: '\\\\]', display: true},
          {left: '\\\\(', right: '\\\\)', display: false}
        ],
        throwOnError: false
      });
      setTimeout(function() { window.print(); }, 500);
    });
  </script>
</body>
</html>`;

    printWindow.document.write(printContent);
    printWindow.document.close();
    toast.success("Print dialog opened - save as PDF");
  };

  const saveToLibrary = async () => {
    if (!user || !hasAccess) {
      toast.error("Please sign in with a membership to save to library");
      return;
    }
    
    const title = saveTitle || documentTitle || "Untitled Document";
    const content = generateFullDocument();

    const { error } = await supabase
      .from('library_items')
      .insert({
        user_id: user.id,
        group_id: selectedGroup,
        result_type: title,
        query: "LaTeX Document Editor",
        solution: content
      });

    if (error) {
      toast.error("Failed to save to library");
    } else {
      toast.success("Saved to library!");
      setIsSaveDialogOpen(false);
      setSaveTitle("");
    }
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">LaTeX Document Editor</h1>
          <p className="text-muted-foreground">
            Create professional lesson plans, assessments, and reports with live LaTeX preview
          </p>
        </div>

        {/* Template Selection */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Start with a Template</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(templates).map(([key, template]) => (
                <Button
                  key={key}
                  variant="outline"
                  className="h-auto py-4 flex flex-col gap-2"
                  onClick={() => applyTemplate(key as keyof typeof templates)}
                >
                  <template.icon className="h-6 w-6" />
                  <span className="text-sm">{template.name}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* SuperCalc Assistant - Full Width */}
        <Card className="mb-6 border-supercalc-brand/40 bg-gradient-to-r from-supercalc-brand/5 via-background to-supercalc-brand/5">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="p-2 rounded-lg bg-supercalc-brand/10">
                  <Brain className="h-6 w-6 text-supercalc-brand" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">SuperCalc Assistant</h3>
                  <p className="text-xs text-muted-foreground">Describe what to generate</p>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col sm:flex-row gap-3">
                <Textarea
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  placeholder="E.g., 'Create a lesson about quadratic equations' or 'Add practice problems on Newton's Laws'"
                  className="min-h-[60px] sm:min-h-[40px] text-sm flex-1 resize-none"
                  disabled={!canUseAI}
                  rows={2}
                />
                
                {canUseAI ? (
                  <Button
                    className="bg-supercalc-brand hover:bg-supercalc-brand/90 whitespace-nowrap"
                    onClick={() => generateWithAI('full_document')}
                    disabled={isGenerating || !aiContext.trim() || !documentTitle}
                  >
                    {isGenerating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Generate Document
                  </Button>
                ) : (
                  <Button asChild className="whitespace-nowrap">
                    <Link to="/membership">
                      <Lock className="h-4 w-4 mr-2" />
                      Unlock AI
                    </Link>
                  </Button>
                )}
              </div>
              
              {!aiContext.trim() && canUseAI && (
                <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3 w-3" />
                  <span>Enter prompt first</span>
                </div>
              )}
              {!documentTitle && aiContext.trim() && canUseAI && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <AlertCircle className="h-3 w-3" />
                  <span>Add title below</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-[2fr_3fr] gap-6">
          {/* Editor Panel */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Document Editor</span>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard} title="Copy LaTeX">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportAsLatex} title="Download .tex">
                      <Download className="h-4 w-4 mr-1" /> .tex
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportAsHtml} title="Download HTML">
                      <Download className="h-4 w-4 mr-1" /> .html
                    </Button>
                    <Button variant="outline" size="sm" onClick={printAsPdf} title="Print/Save as PDF">
                      <Printer className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" disabled={!user || !hasAccess}>
                          <Save className="h-4 w-4 mr-1" /> Save
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Save to Library</DialogTitle>
                          <DialogDescription>
                            Save this document to your library for future reference
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>Document Title</Label>
                            <Input
                              value={saveTitle || documentTitle}
                              onChange={(e) => setSaveTitle(e.target.value)}
                              placeholder="Enter title..."
                            />
                          </div>
                          <div>
                            <Label>Group (optional)</Label>
                            <Select value={selectedGroup || ""} onValueChange={(v) => setSelectedGroup(v || null)}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a group..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="">Ungrouped</SelectItem>
                                {groups.map(g => (
                                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button onClick={saveToLibrary} className="w-full">
                            Save to Library
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Document Title</Label>
                  <Input
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                    placeholder="Enter document title..."
                    className="font-medium"
                  />
                </div>

                {/* Section Tabs */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Sections</Label>
                    <Button variant="outline" size="sm" onClick={addSection}>
                      <Plus className="h-4 w-4 mr-1" /> Add Section
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {sections.map((section, index) => (
                      <Button
                        key={index}
                        variant={activeSection === index ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveSection(index)}
                        className="text-xs"
                      >
                        {section.title || `Section ${index + 1}`}
                      </Button>
                    ))}
                  </div>

                  {/* Active Section Editor */}
                  <div className="space-y-3 p-4 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Input
                        value={sections[activeSection].title}
                        onChange={(e) => updateSection(activeSection, 'title', e.target.value)}
                        placeholder="Section title..."
                        className="font-medium"
                      />
                      {sections.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSection(activeSection)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={sections[activeSection].content}
                      onChange={(e) => updateSection(activeSection, 'content', e.target.value)}
                      placeholder="Enter LaTeX content..."
                      className="font-mono min-h-[200px]"
                    />
                    
                    {/* AI Section Actions */}
                    {canUseAI && (
                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-xs"
                              disabled={isGenerating}
                            >
                              {isGenerating ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Wand2 className="h-3 w-3 mr-1" />
                              )}
                              AI Actions
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => generateWithAI('section')}>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Generate Section
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => generateWithAI('expand')}>
                              <Expand className="h-4 w-4 mr-2" />
                              Expand Content
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => generateWithAI('simplify')}>
                              <Minimize className="h-4 w-4 mr-2" />
                              Simplify
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => generateWithAI('examples')}>
                              <ListPlus className="h-4 w-4 mr-2" />
                              Add Examples
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <span className="text-xs text-muted-foreground self-center">
                          Provide context below first
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Insert */}
                <Card className="bg-muted/50">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm">Quick Insert</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {[
                        { label: "Fraction", code: "\\frac{}{}" },
                        { label: "Sqrt", code: "\\sqrt{}" },
                        { label: "Sum", code: "\\sum_{i=1}^{n}" },
                        { label: "Integral", code: "\\int_{a}^{b}" },
                        { label: "Itemize", code: "\\begin{itemize}\n  \\item \n\\end{itemize}" },
                        { label: "Enumerate", code: "\\begin{enumerate}\n  \\item \n\\end{enumerate}" },
                        { label: "Equation", code: "$$  $$" },
                        { label: "Table", code: "\\begin{tabular}{|c|c|}\n\\hline\n & \\\\\n\\hline\n\\end{tabular}" },
                      ].map((item) => (
                        <Button
                          key={item.label}
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => {
                            const newContent = sections[activeSection].content + item.code;
                            updateSection(activeSection, 'content', newContent);
                          }}
                        >
                          {item.label}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

          </div>

          {/* Preview Panel */}
          <div className="space-y-4 flex flex-col">
            <Card className="sticky top-4 flex flex-col flex-1 min-h-[600px]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Live Preview</CardTitle>
                    <CardDescription>See how your document will look</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Zoom Controls */}
                    <div className="flex items-center gap-1 border rounded-md p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPreviewZoom(Math.max(75, previewZoom - 25))}
                        disabled={previewZoom <= 75}
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <Select value={previewZoom.toString()} onValueChange={(v) => setPreviewZoom(Number(v))}>
                        <SelectTrigger className="h-7 w-16 border-0 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {zoomLevels.map((level) => (
                            <SelectItem key={level} value={level.toString()}>
                              {level}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPreviewZoom(Math.min(150, previewZoom + 25))}
                        disabled={previewZoom >= 150}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Fullscreen Toggle */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setIsFullscreen(true)}
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-4">
                <div 
                  className="h-full overflow-auto border rounded-lg bg-card"
                  style={{ maxHeight: 'calc(100vh - 280px)' }}
                >
                  <div 
                    className="prose dark:prose-invert max-w-none p-6 origin-top-left"
                    style={{ 
                      transform: `scale(${previewZoom / 100})`,
                      transformOrigin: 'top left',
                      width: `${10000 / previewZoom}%`
                    }}
                  >
                    {documentTitle && (
                      <h1 className="text-2xl font-bold text-center mb-6 border-b pb-4">
                        <MathFormula>{documentTitle}</MathFormula>
                      </h1>
                    )}
                    {sections.map((section, index) => (
                      <div key={index} className="mb-6">
                        {section.title && (
                          <h2 className="text-xl font-semibold mb-3">
                            <MathFormula>{section.title}</MathFormula>
                          </h2>
                        )}
                        {section.content && (
                          <div className="whitespace-pre-wrap">
                            <MathFormula>{section.content}</MathFormula>
                          </div>
                        )}
                      </div>
                    ))}
                    {!documentTitle && sections.every(s => !s.content) && (
                      <p className="text-muted-foreground text-center py-12">
                        Start typing or select a template to see the preview
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Library CTA for non-members */}
            {(!user || !hasAccess) && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold">Save Documents to Library</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Upgrade to save your documents, organize them into groups, and access them anytime.
                  </p>
                  <Button asChild>
                    <Link to="/membership">View Membership Plans</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Fullscreen Preview Modal */}
          {isFullscreen && (
            <div className="fixed inset-0 z-50 bg-background flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Document Preview</h2>
                <div className="flex items-center gap-3">
                  {/* Zoom Controls in Fullscreen */}
                  <div className="flex items-center gap-1 border rounded-md p-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setPreviewZoom(Math.max(75, previewZoom - 25))}
                      disabled={previewZoom <= 75}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Select value={previewZoom.toString()} onValueChange={(v) => setPreviewZoom(Number(v))}>
                      <SelectTrigger className="h-7 w-16 border-0 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {zoomLevels.map((level) => (
                          <SelectItem key={level} value={level.toString()}>
                            {level}%
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setPreviewZoom(Math.min(150, previewZoom + 25))}
                      disabled={previewZoom >= 150}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setIsFullscreen(false)}>
                    <X className="h-4 w-4 mr-1" /> Close
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-8">
                <div 
                  className="prose dark:prose-invert max-w-4xl mx-auto bg-card border rounded-lg p-8 shadow-lg origin-top"
                  style={{ 
                    transform: `scale(${previewZoom / 100})`,
                    transformOrigin: 'top center',
                  }}
                >
                  {documentTitle && (
                    <h1 className="text-3xl font-bold text-center mb-8 border-b pb-6">
                      <MathFormula>{documentTitle}</MathFormula>
                    </h1>
                  )}
                  {sections.map((section, index) => (
                    <div key={index} className="mb-8">
                      {section.title && (
                        <h2 className="text-2xl font-semibold mb-4">
                          <MathFormula>{section.title}</MathFormula>
                        </h2>
                      )}
                      {section.content && (
                        <div className="whitespace-pre-wrap text-base leading-relaxed">
                          <MathFormula>{section.content}</MathFormula>
                        </div>
                      )}
                    </div>
                  ))}
                  {!documentTitle && sections.every(s => !s.content) && (
                    <p className="text-muted-foreground text-center py-16">
                      Start typing or select a template to see the preview
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}